import {
  getViewerBufferTabs,
  MAX_VIEWER_BUFFER_TABS,
  MIN_VIEWER_BUFFER_TABS,
} from './workstationLayout';

/**
 * Which viewer window a study should open in, shared across every browser window.
 *
 * WHY THIS IS NOT A PLAIN ARRAY IN MonitorManager
 * -----------------------------------------------
 * Viewer launches reuse fixed window names ('viewer_1'..'viewer_N') so a study can take
 * over a window the radiologist has finished with instead of piling up tabs forever.
 * Choosing which name to use needs to know which of those windows are still open — and
 * that state used to live in a module-scoped array inside MonitorManager.
 *
 * MonitorManager is instantiated once PER BROWSER WINDOW. The reporting editor opens as
 * its own window (openReportingWindow uses '_blank'), so a viewer launched from reporting
 * was tracked by the reporting window's instance, which the PACS list window could not
 * see. Both instances started counting at slot 0, both named their window 'viewer_1', and
 * the study-list "Open Viewer" button silently took over the viewer the radiologist was
 * actively reporting from — losing their scroll position, window/level and layout.
 *
 * localStorage is shared by every window on the origin, so the queue lives here instead.
 *
 * LIVENESS
 * --------
 * `win.closed` only works for a handle the asking window itself opened, which is exactly
 * the cross-window problem again. So each viewer window proves it is alive by writing a
 * heartbeat into its own record (startViewerSlotHeartbeat, called from the app bootstrap
 * in every window) and deletes that record on unload. A slot with a stale heartbeat is
 * free. The heartbeat cannot start until the viewer has booted, so a freshly claimed slot
 * is also protected by a boot grace period — otherwise two quick clicks would both be
 * handed the same slot while the first window was still loading.
 *
 * PINNING
 * -------
 * A viewer launched from the reporting editor is pinned, which takes it out of the
 * recycling pool. That is the actual protection: a radiologist mid-report can open an
 * unrelated study for someone without losing the images they are reporting from. Pins are
 * a preference, not a lock — if every live slot is pinned the least-recently-used one is
 * still recycled, so the buffer can never deadlock and leave a click doing nothing.
 */

interface SlotRecord {
  /** The study this window was last opened with, so the same study reuses its window. */
  studyUID: string | null;
  /** Launched from the reporting editor: protected from recycling while anything else is free. */
  pinned: boolean;
  /** Date.now() of the last launch into this slot — the LRU key. */
  lastUsed: number;
  /** Date.now() of the last beat from the window itself. */
  heartbeat: number;
  /** Date.now() of the launch, so a still-booting window is not mistaken for a dead one. */
  claimedAt: number;
}

type Registry = Record<string, SlotRecord>;

const STORAGE_KEY = 'pacs_viewer_slots';

/** How often a live viewer window republishes its heartbeat. */
const HEARTBEAT_MS = 2000;

/** Four missed beats before a window is presumed gone (crash, or killed by the OS). */
const STALE_MS = 8000;

/**
 * A just-claimed slot counts as alive even without a heartbeat, covering the gap between
 * window.open and the new window executing its bootstrap. The heartbeat starts in
 * index.js before React mounts, so this only has to cover script load, not DICOM load.
 */
const BOOT_GRACE_MS = 15000;

const SLOT_NAME_RE = /^viewer_(\d+)$/;

const slotName = (index: number): string => `viewer_${index}`;

const readRegistry = (): Registry => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Corrupt JSON, or storage blocked on a locked-down browser profile. Losing the
    // registry degrades to the old behaviour (windows get recycled sooner than ideal);
    // it never breaks a launch.
    return {};
  }
};

const writeRegistry = (registry: Registry): void => {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(registry));
  } catch (e) {
    console.warn('viewerSlotRegistry: could not persist slot registry', e);
  }
};

const readRaw = (): string => {
  try {
    return window.localStorage.getItem(STORAGE_KEY) || '';
  } catch {
    return '';
  }
};

/**
 * Read-modify-write, retried if another window wrote while we were deciding.
 *
 * localStorage has no compare-and-swap, and a blind read-modify-write here is not a
 * theoretical hazard: signing off a report tears down two windows at once — the reporting
 * window unpins its slot while the viewer deletes the very same record — so the two
 * collide on every sign-off, not on some freak interleaving. Losing that one resolves as
 * a deleted record being written back, i.e. a closed window still holding its name.
 *
 * Re-reading the raw string immediately before writing shrinks the vulnerable gap to two
 * adjacent statements. It is not a real lock, but nothing here is worth a lock: every
 * record is self-repairing, since a live window re-announces itself on the next heartbeat
 * and a dead one ages out.
 */
const mutate = <T>(fn: (registry: Registry) => T): T => {
  let result!: T;

  for (let attempt = 0; attempt < 3; attempt++) {
    const before = readRaw();
    const registry = readRegistry();
    result = fn(registry);

    // Someone else wrote while fn() was running — their change is in neither our copy nor
    // our result, so start over on the value they left behind.
    if (readRaw() !== before) continue;

    writeRegistry(registry);
    return result;
  }

  // Persistently contended (three windows launching at once). The last result still
  // describes a sane slot choice; only the bookkeeping is skipped.
  return result;
};

const isAlive = (record?: SlotRecord): boolean => {
  if (!record) return false;
  const now = Date.now();
  return now - record.heartbeat < STALE_MS || now - record.claimedAt < BOOT_GRACE_MS;
};

const clampBufferCount = (raw: any): number => {
  const n = Math.floor(Number(raw));
  if (!Number.isFinite(n)) return 1;
  return Math.min(Math.max(n, MIN_VIEWER_BUFFER_TABS), MAX_VIEWER_BUFFER_TABS);
};

/**
 * This deployment's default number of viewer windows (ENV_CONFIG.VIEWER_BUFFER_TABS),
 * falling back to a single shared window — the behaviour that predates buffering. Exported
 * so the profile screen can name the value a user gets when they do not override it.
 */
export const getCentreViewerBufferCount = (): number =>
  clampBufferCount((window as any).ENV_CONFIG?.VIEWER_BUFFER_TABS);

/**
 * How many viewer windows this user gets: their own preference when they have set one,
 * otherwise the centre default.
 */
export const getViewerBufferCount = (): number => {
  const preferred = getViewerBufferTabs();
  return preferred === null ? getCentreViewerBufferCount() : clampBufferCount(preferred);
};

/** Least-recently-used of the given slots, or undefined when none were given. */
const lruOf = (names: string[], registry: Registry): string | undefined =>
  names.reduce<string | undefined>((best, name) => {
    if (!best) return name;
    return (registry[name]?.lastUsed ?? 0) < (registry[best]?.lastUsed ?? 0) ? name : best;
  }, undefined);

/**
 * Pick the window name a study should open in, and record the claim.
 *
 * Returns null when buffering is switched off (a count of 1), in which case the caller
 * keeps its legacy target and nothing is tracked.
 *
 * Preference order:
 *   1. the window already showing this study        — reuse it, no reload
 *   2. any slot whose window is closed or never opened — fill the gap before evicting
 *   3. least-recently-used UNPINNED window          — normal recycling
 *   4. least-recently-used window of any kind       — everything is pinned; never stall
 */
export const acquireViewerSlot = (
  studyUID: string,
  opts: { pin?: boolean } = {}
): string | null => {
  const count = getViewerBufferCount();
  if (count <= 1) return null;

  return mutate(registry => {
    const names = Array.from({ length: count }, (_, i) => slotName(i + 1));

    // Forget anything outside the current pool once its window is gone — the user may
    // have lowered their limit, and dead records should not accumulate in storage.
    Object.keys(registry).forEach(name => {
      const match = SLOT_NAME_RE.exec(name);
      if (!match || (Number(match[1]) > count && !isAlive(registry[name]))) {
        delete registry[name];
      }
    });

    const sameStudy = names.find(
      name => registry[name]?.studyUID === studyUID && isAlive(registry[name])
    );
    const chosen =
      sameStudy ||
      names.find(name => !isAlive(registry[name])) ||
      lruOf(names.filter(name => !registry[name]?.pinned), registry) ||
      lruOf(names, registry)!;

    const now = Date.now();
    registry[chosen] = {
      studyUID,
      // Reopening the SAME study keeps whatever protection the slot already had, so a
      // study-list click on the study being reported does not quietly unpin it. Any other
      // study taking the slot over means it is no longer the reporting viewer.
      pinned: opts.pin === true || (!!sameStudy && !!registry[chosen]?.pinned),
      lastUsed: now,
      heartbeat: now,
      claimedAt: now,
    };

    return chosen;
  });
};

/**
 * Give a slot back. Called when window.open returned null (popup blocked), so a launch
 * that never happened does not hold a window name hostage for the boot grace period.
 */
export const releaseViewerSlot = (name: string | null): void => {
  if (!name) return;
  mutate(registry => {
    delete registry[name];
  });
};

/**
 * Drop a slot's protection without disturbing the window itself. Called when the reporting
 * window that pinned it goes away, so its viewer rejoins the normal recycling pool instead
 * of being protected forever.
 */
export const unpinViewerSlot = (name: string | null): void => {
  if (!name) return;
  mutate(registry => {
    if (registry[name]) registry[name].pinned = false;
  });
};

/**
 * Publish this window's heartbeat, if it is a viewer occupying a buffer slot.
 *
 * Called from the app bootstrap in EVERY window; it identifies a viewer by the window name
 * the launcher assigned, so nothing has to be threaded through the viewer's own routing.
 * A no-op in the PACS list, the reporting editor, and in a viewer opened outside the
 * buffer pool (a plain '_blank' series viewer, or buffering switched off).
 */
export const startViewerSlotHeartbeat = (): void => {
  const name = (typeof window !== 'undefined' && window.name) || '';
  if (!SLOT_NAME_RE.test(name)) return;

  const beat = () =>
    mutate(registry => {
      const now = Date.now();
      const record = registry[name];
      if (record) {
        record.heartbeat = now;
        return;
      }
      // The record was pruned while this window was open (storage cleared on logout, or a
      // lost write). Re-announce so the window is not handed to another study underneath
      // the radiologist; the study UID is unknown from here, which only costs us the
      // same-study reuse shortcut.
      registry[name] = {
        studyUID: null,
        pinned: false,
        lastUsed: now,
        heartbeat: now,
        claimedAt: now,
      };
    });

  beat();
  window.setInterval(beat, HEARTBEAT_MS);

  // Frees the slot the instant the viewer goes away, rather than after the stale timeout.
  // Also fires on reload, where the beat above immediately re-claims it.
  //
  // Listening for both events on purpose. The auto-close on sign-off reaches this window
  // as a scripted window.close() (see customApp's CLOSE_TAB listener), and 'pagehide' is
  // the one that fires dependably for a teardown the user did not initiate. Releasing
  // twice is harmless; releasing never would just leave the slot held until its heartbeat
  // went stale.
  //
  // `persisted` means the page went into the back/forward cache and may yet come back, so
  // the window still owns its name and the slot must not be handed out. Timers are frozen
  // in that state, but beat() re-announces the slot if it ever resumes.
  window.addEventListener('beforeunload', () => releaseViewerSlot(name));
  window.addEventListener('pagehide', event => {
    if (!event.persisted) releaseViewerSlot(name);
  });
};
